import { useState, useRef } from 'react';
import { FlatList } from 'react-native';
import type { MatchedAction, ActionTemplate } from '@utils/semanticActionMapper';

export const useEasyModeState = () => {
  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [mode, setMode] = useState('ask');
  const [suggestedActions, setSuggestedActions] = useState([]);
  
  // Refs
  const inputRef = useRef(null);
  const isProcessingRef = useRef(false); // Guard to prevent double submissions
  const timeoutIdsRef = useRef<NodeJS.Timeout[]>([]); // Track all timeout IDs for cleanup
  const isClosingRef = useRef(false); // Guard to prevent multiple simultaneous close/navigation attempts
  const chatFingerprintRef = useRef<string | null>(null); // Track current chat fingerprint to detect new chats
  const scrollViewRef = useRef<FlatList>(null);
  const inputToolbarTextRef = useRef<string>('');

  // Semantic Action Mapper state
  const [pendingActions, setPendingActions] = useState<MatchedAction[]>([]);
  const [actionRequiringData, setActionRequiringData] = useState<ActionTemplate | null>(null);
  const [isMapperInitialized, setIsMapperInitialized] = useState(false);
  const [extractedFormData, setExtractedFormData] = useState<Record<string, any>>({});
  const [currentUserQuery, setCurrentUserQuery] = useState<string>('');
  const [showPostExecutionFeedback, setShowPostExecutionFeedback] = useState(false);
  const [lastExecutedAction, setLastExecutedAction] = useState<{ template: ActionTemplate; data?: any } | null>(null);

  // Report Dashboard State
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>([]);
  const [reportDashboardConfig, setReportDashboardConfig] = useState<any>(null);
  const [pendingReportData, setPendingReportData] = useState<any>(null);

  // Drag and Drop State
  const [draggedOver, setDraggedOver] = useState(false);
  const [metadataDialogVisible, setMetadataDialogVisible] = useState(false);
  const [pendingMediaFile, setPendingMediaFile] = useState<any>(null);
  const [mediaMetadata, setMediaMetadata] = useState({
    description: '',
    location: '',
    scheduledDate: '',
    tags: ''
  });

  // Image Analysis State (Easy Mode only)
  const [pendingImageAnalysis, setPendingImageAnalysis] = useState<{
    imageFile: File;
    imageData: string; // base64
    analysis: any;
  } | null>(null);

  // Excel Multi-Sheet Selection State
  const [availableSheets, setAvailableSheets] = useState<string[]>([]);
  const [showSheetSelector, setShowSheetSelector] = useState(false);
  const [pendingWorkbook, setPendingWorkbook] = useState<any>(null);
  const [pendingFileName, setPendingFileName] = useState<string>('');

  // Data models state
  const [userDataForms, setUserDataForms] = useState<any[]>([]);
  const [selectedDataModel, setSelectedDataModel] = useState<any | null>(null);

  // Additional refs for tracking
  const dismissedPromptsLoadedRef = useRef(false);
  const routeParamsProcessedRef = useRef<string | null>(null);
  const hierarchyRefetchAttemptedRef = useRef(false);

  return {
    // Chat state
    chatMessages,
    setChatMessages,
    inputDisabled,
    setInputDisabled,
    mode,
    setMode,
    suggestedActions,
    setSuggestedActions,
    
    // Refs
    inputRef,
    isProcessingRef,
    timeoutIdsRef,
    isClosingRef,
    chatFingerprintRef,
    scrollViewRef,
    inputToolbarTextRef,

    // Semantic Action Mapper state
    pendingActions,
    setPendingActions,
    actionRequiringData,
    setActionRequiringData,
    isMapperInitialized,
    setIsMapperInitialized,
    extractedFormData,
    setExtractedFormData,
    currentUserQuery,
    setCurrentUserQuery,
    showPostExecutionFeedback,
    setShowPostExecutionFeedback,
    lastExecutedAction,
    setLastExecutedAction,

    // Report Dashboard State
    selectedDataSources,
    setSelectedDataSources,
    reportDashboardConfig,
    setReportDashboardConfig,
    pendingReportData,
    setPendingReportData,

    // Drag and Drop State
    draggedOver,
    setDraggedOver,
    metadataDialogVisible,
    setMetadataDialogVisible,
    pendingMediaFile,
    setPendingMediaFile,
    mediaMetadata,
    setMediaMetadata,

    // Image Analysis State
    pendingImageAnalysis,
    setPendingImageAnalysis,

    // Excel Multi-Sheet Selection State
    availableSheets,
    setAvailableSheets,
    showSheetSelector,
    setShowSheetSelector,
    pendingWorkbook,
    setPendingWorkbook,
    pendingFileName,
    setPendingFileName,

    // Data models state
    userDataForms,
    setUserDataForms,
    selectedDataModel,
    setSelectedDataModel,

    // Additional refs
    dismissedPromptsLoadedRef,
    routeParamsProcessedRef,
    hierarchyRefetchAttemptedRef,
  };
};




